/**
 * Safe expression evaluator for the Expression module.
 *
 * A closed grammar — numbers, strings, + - * / %, parentheses, unary -/!,
 * comparisons, && || , ternary ?: , and whitelisted function calls. No loops,
 * no assignment, no property access, no `eval`. Always terminates. Any parse or
 * runtime error returns "" so the engine never throws.
 *
 * Identifiers (e.g. {{Price}}) are expected to be substituted by the caller
 * BEFORE evaluation; a bare identifier that isn't a known function name
 * evaluates to its own text as a string fallback.
 */

const MAX_LEN = 2000;
const MAX_NODES = 500;

type Fns = Record<string, (...args: any[]) => unknown>;
export type EvalOptions = { fns?: Fns };

// ---- value coercion -------------------------------------------------------
function toNumber(v: unknown): number {
  if (typeof v === "number") return v;
  if (typeof v === "boolean") return v ? 1 : 0;
  if (v == null) return NaN;
  const s = String(v).trim();
  const direct = Number(s);
  if (Number.isFinite(direct)) return direct;
  // strip commas/currency: keep digits, one dot, leading -
  let cleaned = "";
  let dot = false;
  for (const ch of s) {
    if (ch >= "0" && ch <= "9") cleaned += ch;
    else if (ch === "." && !dot) { cleaned += ch; dot = true; }
    else if (ch === "-" && cleaned === "") cleaned += ch;
  }
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : NaN;
}
function toStr(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "number") {
    if (!Number.isFinite(v)) return "";
    return String(v);
  }
  if (typeof v === "boolean") return v ? "true" : "false";
  return String(v);
}

// ---- tokenizer ------------------------------------------------------------
type Tok =
  | { t: "num"; v: number }
  | { t: "str"; v: string }
  | { t: "id"; v: string }
  | { t: "op"; v: string };

const OPS = ["===", "!==", "==", "!=", ">=", "<=", "&&", "||", ">", "<", "+", "-", "*", "/", "%", "(", ")", ",", "?", ":", "!"];

function tokenize(src: string): Tok[] {
  const toks: Tok[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i];
    if (c === " " || c === "\t" || c === "\n" || c === "\r") { i++; continue; }
    if (c === '"' || c === "'") {
      const q = c; i++; let s = "";
      while (i < src.length && src[i] !== q) {
        if (src[i] === "\\" && i + 1 < src.length) { s += src[i + 1]; i += 2; continue; }
        s += src[i]; i++;
      }
      i++; // closing quote
      toks.push({ t: "str", v: s });
      continue;
    }
    if ((c >= "0" && c <= "9") || (c === "." && src[i + 1] >= "0" && src[i + 1] <= "9")) {
      let s = ""; while (i < src.length && /[0-9.]/.test(src[i])) { s += src[i]; i++; }
      toks.push({ t: "num", v: Number(s) });
      continue;
    }
    if (/[A-Za-z_]/.test(c)) {
      let s = ""; while (i < src.length && /[A-Za-z0-9_]/.test(src[i])) { s += src[i]; i++; }
      toks.push({ t: "id", v: s });
      continue;
    }
    const op = OPS.find((o) => src.startsWith(o, i));
    if (!op) throw new Error("bad char " + c);
    toks.push({ t: "op", v: op });
    i += op.length;
  }
  return toks;
}

// ---- Pratt parser → AST ---------------------------------------------------
type Node =
  | { k: "num"; v: number }
  | { k: "str"; v: string }
  | { k: "id"; v: string }
  | { k: "un"; op: string; a: Node }
  | { k: "bin"; op: string; a: Node; b: Node }
  | { k: "tern"; c: Node; a: Node; b: Node }
  | { k: "call"; name: string; args: Node[] };

const BIN_PREC: Record<string, number> = {
  "||": 1, "&&": 2,
  "==": 3, "!=": 3, "===": 3, "!==": 3, ">": 4, "<": 4, ">=": 4, "<=": 4,
  "+": 5, "-": 5, "*": 6, "/": 6, "%": 6,
};

function parse(toks: Tok[]): Node {
  let pos = 0;
  let nodeCount = 0;
  const bump = () => { if (++nodeCount > MAX_NODES) throw new Error("too complex"); };
  const peek = () => toks[pos];
  const eat = () => toks[pos++];
  const expectOp = (v: string) => {
    const t = eat();
    if (!t || t.t !== "op" || t.v !== v) throw new Error("expected " + v);
  };

  function parsePrimary(): Node {
    bump();
    const t = eat();
    if (!t) throw new Error("eof");
    if (t.t === "num") return { k: "num", v: t.v };
    if (t.t === "str") return { k: "str", v: t.v };
    if (t.t === "op" && t.v === "(") { const e = parseExpr(0); expectOp(")"); return e; }
    if (t.t === "op" && (t.v === "-" || t.v === "!")) return { k: "un", op: t.v, a: parsePrimary() };
    if (t.t === "id") {
      if (peek() && peek().t === "op" && (peek() as any).v === "(") {
        eat(); // (
        const args: Node[] = [];
        if (!(peek() && peek().t === "op" && (peek() as any).v === ")")) {
          args.push(parseExpr(0));
          while (peek() && peek().t === "op" && (peek() as any).v === ",") { eat(); args.push(parseExpr(0)); }
        }
        expectOp(")");
        return { k: "call", name: t.v, args };
      }
      return { k: "id", v: t.v };
    }
    throw new Error("unexpected token");
  }

  function parseExpr(minPrec: number): Node {
    let left = parsePrimary();
    for (;;) {
      const t = peek();
      if (!t || t.t !== "op") break;
      if (t.v === "?") {
        if (minPrec > 0) break;
        eat();
        const a = parseExpr(0); expectOp(":"); const b = parseExpr(0);
        left = { k: "tern", c: left, a, b };
        continue;
      }
      const prec = BIN_PREC[t.v];
      if (prec === undefined || prec < minPrec) break;
      eat();
      const right = parseExpr(prec + 1);
      bump();
      left = { k: "bin", op: t.v, a: left, b: right };
    }
    return left;
  }

  const out = parseExpr(0);
  if (pos !== toks.length) throw new Error("trailing tokens");
  return out;
}

// ---- evaluate -------------------------------------------------------------
function evalNode(n: Node, fns: Fns): unknown {
  switch (n.k) {
    case "num": return n.v;
    case "str": return n.v;
    case "id": return n.v; // bare identifier → its own text (caller should have substituted)
    case "un":
      if (n.op === "-") return -toNumber(evalNode(n.a, fns));
      return toNumber(evalNode(n.a, fns)) === 0 ? 1 : 0;
    case "tern":
      return truthy(evalNode(n.c, fns)) ? evalNode(n.a, fns) : evalNode(n.b, fns);
    case "call": {
      const fn = fns[n.name];
      if (typeof fn !== "function") throw new Error("unknown fn " + n.name);
      return fn(...n.args.map((a) => evalNode(a, fns)));
    }
    case "bin": {
      const op = n.op;
      if (op === "&&") return truthy(evalNode(n.a, fns)) ? evalNode(n.b, fns) : evalNode(n.a, fns);
      if (op === "||") return truthy(evalNode(n.a, fns)) ? evalNode(n.a, fns) : evalNode(n.b, fns);
      const a = evalNode(n.a, fns);
      const b = evalNode(n.b, fns);
      switch (op) {
        case "+": {
          // numeric when both coerce to finite numbers; else string concat
          const an = toNumber(a), bn = toNumber(b);
          if (Number.isFinite(an) && Number.isFinite(bn)) return an + bn;
          return toStr(a) + toStr(b);
        }
        case "-": return toNumber(a) - toNumber(b);
        case "*": return toNumber(a) * toNumber(b);
        case "/": { const d = toNumber(b); return d === 0 ? NaN : toNumber(a) / d; }
        case "%": { const d = toNumber(b); return d === 0 ? NaN : toNumber(a) % d; }
        case "==": case "===": return looseEq(a, b) ? 1 : 0;
        case "!=": case "!==": return looseEq(a, b) ? 0 : 1;
        case ">": return toNumber(a) > toNumber(b) ? 1 : 0;
        case "<": return toNumber(a) < toNumber(b) ? 1 : 0;
        case ">=": return toNumber(a) >= toNumber(b) ? 1 : 0;
        case "<=": return toNumber(a) <= toNumber(b) ? 1 : 0;
        default: throw new Error("bad op");
      }
    }
  }
}
function truthy(v: unknown): boolean {
  if (typeof v === "number") return v !== 0 && Number.isFinite(v);
  if (typeof v === "string") return v.trim().length > 0;
  return Boolean(v);
}
function looseEq(a: unknown, b: unknown): boolean {
  const an = toNumber(a), bn = toNumber(b);
  if (Number.isFinite(an) && Number.isFinite(bn)) return an === bn;
  return toStr(a) === toStr(b);
}

export function evaluateExpression(src: string, opts: EvalOptions = {}): string {
  try {
    if (typeof src !== "string" || src.length === 0 || src.length > MAX_LEN) return "";
    const toks = tokenize(src);
    if (toks.length === 0) return "";
    const ast = parse(toks);
    const result = evalNode(ast, opts.fns ?? {});
    return toStr(result);
  } catch {
    return "";
  }
}
